import { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, FormLabel, Stack, Typography } from "@mui/material";
import { Link, useHistory } from "react-router-dom";
import { TextValidator, ValidatorForm } from "react-material-ui-form-validator";
import { authService } from "Apis/api";
import { WashedForm } from "Components/UI/WashedForm";
import { useNotification } from "Hooks";

interface IPasswordResetProps {
  code: string;
  isInitial?: boolean;
}

const PasswordReset = ({ code, isInitial }: IPasswordResetProps) => {
  const [newPassword, setNewPassword] = useState("");
  const [newRetypePassword, setNewRetypePassword] = useState("");
  const [inProgress, setInProgress] = useState(false);
  const submittingRef = useRef(false);
  const history = useHistory();
  const { showErrorNotification } = useNotification();

  useEffect(() => {
    ValidatorForm.addValidationRule("isPasswordMatch", (value) => {
      if (value !== newPassword) {
        return false;
      }
      return true;
    });
    return () => {
      ValidatorForm.removeValidationRule("isPasswordMatch");
    };
  }, [newPassword]);

  const handleSubmit = useCallback(async () => {
    if (submittingRef.current) {
      return;
    }
    submittingRef.current = true;
    setInProgress(true);
    try {
      await authService.authChangePasswordWithResetCode({
        body: {
          code,
          password: newPassword,
          confirmation: newRetypePassword,
        },
      });
      history.push("/login");
    } catch (e) {
      submittingRef.current = false;
      setInProgress(false);
      showErrorNotification("Internal Server Error");
    }
  }, [code, newPassword, newRetypePassword, history, showErrorNotification]);

  return (
    <Stack spacing={3} alignItems="center">
      {isInitial && (
        <Typography color={"white"} variant="subtitle1" mb={1}>
          Welcome to Incendium. To get started, please set a secure password
        </Typography>
      )}

      <WashedForm>
        <ValidatorForm onSubmit={handleSubmit}>
          <Stack spacing={4}>
            <Stack>
              <FormLabel>
                {isInitial ? "Set Password" : "New Password"}
              </FormLabel>
              <TextValidator
                type="password"
                name="password"
                value={newPassword}
                onChange={(e: React.FormEvent<HTMLInputElement>) =>
                  setNewPassword(e.currentTarget.value)
                }
                variant="outlined"
                fullWidth
                disabled={inProgress}
                validators={["required"]}
                errorMessages={["Password is required"]}
              />
            </Stack>
            <Stack>
              <FormLabel>Confirm Password</FormLabel>
              <TextValidator
                type="password"
                name="confirmation"
                value={newRetypePassword}
                onChange={(e: React.FormEvent<HTMLInputElement>) =>
                  setNewRetypePassword(e.currentTarget.value)
                }
                variant="outlined"
                fullWidth
                disabled={inProgress}
                validators={["required", "isPasswordMatch"]}
                errorMessages={["Password is required", "Passwords must match"]}
              />
            </Stack>
          </Stack>
          <Box mt={4} mb={2}>
            <Button
              type="submit"
              variant="contained"
              disabled={inProgress}
              color="primary"
              fullWidth
              size="large"
            >
              Submit
            </Button>
          </Box>
          <Box mt={1} display={"flex"} justifyContent={"flex-start"}>
            <Typography color={"white"} fontWeight={500}>
              <Link to="/login">{"<"} Go Back</Link>
            </Typography>
          </Box>
        </ValidatorForm>
      </WashedForm>
    </Stack>
  );
};
export default PasswordReset;
